import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { 
  ShieldCheck, 
  Clock, 
  Bike, 
  Zap, 
  Shield, 
  AlertCircle, 
  ArrowRight,
  CheckCircle2,
  Loader2,
  IndianRupee,
  MapPin,
  Smartphone,
  Sparkles
} from "lucide-react";
import { api } from "../services/api";

const Price: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId, variantId } = useParams();

  const [finalPrice, setFinalPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const { groupId, globalAnswers, defectAnswers, functionalAnswers, accessoryAnswers, deviceAge } = location.state || {};

  useEffect(() => {
    window.scrollTo(0, 0);
    api.post("/inspection/calculate-price", {
      variant_id: variantId,
      globalAnswers: globalAnswers || {},
      defect_answers: defectAnswers || [],
      functional_answers: functionalAnswers || [],
      accessory_answers: accessoryAnswers || [],
      device_age: deviceAge || [],
    })
    .then((res) => {
      const calculatedPrice = res.data?.final_price || res.final_price;
      setFinalPrice(calculatedPrice);
    })
    .catch(() => navigate("/"))
    .finally(() => setLoading(false));
  }, [variantId, navigate]);

  if (loading || finalPrice === null) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#363C90] animate-spin mb-3" />
        <p className="text-[10px] font-black uppercase tracking-[3px] text-slate-400 italic">Syncing Market Rates...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      
      {/* --- SLIM COMPACT HEADER --- */}
      <div className="bg-[#363C90] py-10 px-6 relative overflow-hidden">
         <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
         <div className="max-w-[1000px] mx-auto relative z-10 flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-md px-3 py-1 rounded-full border border-emerald-500/30 mb-4">
               <Sparkles size={12} className="text-emerald-400" />
               <span className="text-[9px] font-black uppercase tracking-widest text-emerald-100">Evaluation Ready</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">Great choice, Jaidayal!</h1>
         </div>
      </div>

      {/* --- MAIN CONTENT AREA --- */}
      <div className="max-w-[1000px] mx-auto px-6 -mt-8 relative z-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* LEFT: Price & Trust */}
          <div className="lg:col-span-7 space-y-4">
            
            {/* COMPACT PRICE CARD */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 group">
               <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-[2px]">Guaranteed Quote</span>
                  <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider">
                    <ShieldCheck size={14} /> AI Verified
                  </div>
               </div>

               <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-slate-400">₹</span>
                  <h2 className="text-5xl md:text-6xl font-black text-[#363C90] tracking-tighter">
                    {Number(finalPrice).toLocaleString('en-IN')}
                  </h2>
               </div>

               <div className="mt-8 pt-6 border-t border-slate-50 flex gap-4">
                  <div className="flex items-center gap-2 text-slate-500 font-bold text-[11px] bg-slate-50 px-3 py-1.5 rounded-lg">
                    <Clock size={14} className="text-[#363C90]" /> Valid 24h
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 font-bold text-[11px] bg-slate-50 px-3 py-1.5 rounded-lg">
                    <CheckCircle2 size={14} className="text-emerald-500" /> Fixed Price
                  </div>
               </div>
            </div>

            {/* SLIM TRUST ITEMS */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-6">
               <TrustItemSmall icon={<Bike size={18} />} title="Free Pickup" color="blue" />
               <TrustItemSmall icon={<Zap size={18} />} title="Instant Pay" color="emerald" />
               <TrustItemSmall icon={<Shield size={18} />} title="Data Secure" color="amber" />
            </div>
          </div>

          {/* RIGHT: ACTION SIDEBAR */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm sticky top-6">
              <h3 className="text-sm font-black text-slate-900 mb-4 uppercase tracking-tight">Complete Booking</h3>
              
              <div className="space-y-2 mb-6">
                <StepItemSmall icon={<MapPin size={14} />} text="Pickup Address" />
                <StepItemSmall icon={<Smartphone size={14} />} text="Device Readiness" />
                <StepItemSmall icon={<IndianRupee size={14} />} text="Bank/UPI Details" />
              </div>

              <button
                onClick={() => navigate("/sell/address", { state: { ...location.state, finalPrice } })}
                className="w-full py-4 bg-[#363C90] hover:bg-[#2a2f70] text-white rounded-2xl font-black text-xs flex items-center justify-center gap-2 transition-all hover:translate-y-[-2px] shadow-lg shadow-indigo-900/20 uppercase tracking-[2px]"
              >
                Schedule Pickup <ArrowRight size={16} />
              </button>

              <div className="mt-4 flex gap-2 items-start opacity-60">
                <AlertCircle size={14} className="text-slate-400 shrink-0" />
                <p className="text-[10px] text-slate-500 font-bold uppercase leading-tight italic">
                  Keep ID proof ready for verification.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// --- COMPACT HELPERS ---

const TrustItemSmall = ({ icon, title, color }: any) => {
  const colors: any = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600"
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 ${colors[color]} rounded-lg flex items-center justify-center shrink-0`}>
        {icon}
      </div>
      <span className="font-black text-slate-700 text-[11px] uppercase tracking-tight leading-none">{title}</span>
    </div>
  );
};

const StepItemSmall = ({ icon, text }: any) => (
  <div className="flex items-center gap-3 p-2.5 bg-slate-50/50 rounded-xl border border-slate-50/50">
    <div className="text-[#363C90] opacity-70">{icon}</div>
    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wide">{text}</span>
  </div>
);

export default Price;